"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ApiError,
  api,
  loadTokens,
  type CommentAuthorPosition,
  type CommentRow,
} from "@/lib/api";
import { MentionParser } from "@/components/social/MentionParser";
import { LinkPreviewCard } from "@/components/posts/LinkPreviewCard";
import { MarketCommentOverflowMenu } from "@/components/social/MarketCommentOverflowMenu";
import { extractFirstPlainUrl } from "@/lib/post-link-extract";
import { Skeleton } from "@/components/Skeleton";

interface CommentsSectionProps {
  marketId: string;
  marketSlug: string;
}

/**
 * EPIC-006 STORY-601 + STORY-602 — threaded comments + voting on a
 * market detail page.
 *
 * Reads via the auth-required `/v1/me/markets/:id/comments` endpoint
 * when signed in (so myVote enrichment lands), falls back to the
 * public `/v1/markets/:id/comments` endpoint when signed out.
 *
 * Threading: roots load top-level via the list endpoint; replies
 * lazy-load under each root on "show replies" click. Soft-deleted
 * and admin-hidden comments render as "[removed]" / "[removed by
 * moderator]" so the thread structure is preserved.
 *
 * @market mention parsing (STORY-608) lives in CommentBody — drops
 * into this same component on the next PR.
 */
export function CommentsSection({ marketId, marketSlug }: CommentsSectionProps) {
  const { t } = useTranslation();
  const [authed, setAuthed] = useState<boolean | null>(null);
  // Viewer's user id, fetched once when authed flips true. Used to
  // gate the per-comment Delete button so the author can actually
  // delete their own comment (was hard-coded null before, hiding
  // Delete from everyone including the author).
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  // Slice 3 part B — Stocktwits-parity sort modes:
  //   new      — created_at DESC (default)
  //   top      — score DESC
  //   active   — updated_at DESC (any vote/reply/moderation bumps it)
  //   trending — score DESC over the last 7 days
  const [sort, setSort] = useState<"new" | "top" | "active" | "trending">(
    "new",
  );
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compose form state.
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Slice 2A — per-comment opt-out from the verifiable position
  // badge. Default false (badge shown when user has a position +
  // global pref allows). Resets to false after each successful post.
  const [postAnonymously, setPostAnonymously] = useState(false);

  useEffect(() => {
    const isAuthed = loadTokens() !== null;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthed(isAuthed);
    if (isAuthed) {
      let cancelled = false;
      void api
        .me()
        .then((me) => {
          if (!cancelled) setCurrentUserId(me.id);
        })
        .catch(() => {
          // Non-fatal — only affects per-comment Delete visibility.
        });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (authed === null) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    void (authed
      ? api.listMyMarketComments(marketId, { sort })
      : api.listMarketComments(marketId, { sort })
    )
      .then((res) => {
        if (cancelled) return;
        setComments(res.comments);
        setNextCursor(res.nextCursor);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("comments.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, marketId, sort]);

  async function loadMore() {
    if (!nextCursor || loading) return;
    setLoading(true);
    try {
      const res = await (authed
        ? api.listMyMarketComments(marketId, { sort, cursor: nextCursor })
        : api.listMarketComments(marketId, { sort, cursor: nextCursor }));
      setComments((prev) => [...prev, ...res.comments]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("comments.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    const trimmed = body.trim();
    if (!trimmed || submitting || !authed) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.createMarketComment(marketId, {
        body: trimmed,
        postAnonymously: postAnonymously || undefined,
      });
      setComments((prev) => [created, ...prev]);
      setBody("");
      setPostAnonymously(false);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("comments.postFailed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVote(commentId: string, value: 1 | -1) {
    if (!authed) {
      setError(t("comments.signInToVote"));
      return;
    }
    // Optimistic flip: compute the next state before the round-trip,
    // roll back on error.
    const before = comments.map((c) => ({ ...c }));
    setComments((prev) =>
      prev.map((c) => (c.id === commentId ? applyOptimisticVote(c, value) : c)),
    );
    try {
      const result = await api.voteOnComment(commentId, value);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, score: result.score, myVote: result.myVote }
            : c,
        ),
      );
    } catch (err) {
      setComments(before);
      setError(err instanceof Error ? err.message : t("comments.voteFailed"));
    }
  }

  async function handleDelete(commentId: string) {
    if (!authed) return;
    if (!window.confirm(t("comments.confirmDelete"))) return;
    try {
      await api.deleteComment(commentId);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, body: null, isDeleted: true }
            : c,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t("comments.deleteFailed"));
    }
  }

  return (
    <section className="mb-6">
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
          {t("comments.discussion")}
        </h2>
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          <SortPill
            active={sort === "new"}
            onClick={() => setSort("new")}
            label={t("comments.sortNewest")}
          />
          <SortPill
            active={sort === "top"}
            onClick={() => setSort("top")}
            label={t("comments.sortTop")}
          />
          <SortPill
            active={sort === "active"}
            onClick={() => setSort("active")}
            label={t("comments.sortActive")}
          />
          <SortPill
            active={sort === "trending"}
            onClick={() => setSort("trending")}
            label={t("comments.sortTrending")}
          />
        </div>
      </div>

      {/* Compose box */}
      {authed ? (
        <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          {/* EPIC-043 spec — client-side cap at the backend code
              ceiling (5000). The admin-tunable default lives at 1000
              (`feed.comment_max_chars`); typing past that produces a
              friendly server-side error, but capping at the absolute
              ceiling keeps users from losing pasted content. */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 5_000))}
            disabled={submitting}
            rows={3}
            placeholder={t("comments.addToDiscussion")}
            className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-[var(--color-text3)]">
                {t("comments.charCounter", { count: body.length })}
              </span>
              {/* Slice 2A — per-comment opt-out from the verifiable
                  position badge. */}
              <label
                className="flex cursor-pointer select-none items-center gap-1.5 text-[10px] text-[var(--color-text3)]"
                title={t("comments.anonTitle")}
              >
                <input
                  type="checkbox"
                  checked={postAnonymously}
                  onChange={(e) => setPostAnonymously(e.target.checked)}
                  disabled={submitting}
                  className="h-3 w-3 cursor-pointer accent-[var(--color-brand)]"
                />
                {t("comments.postAnonymously")}
              </label>
            </div>
            <button
              type="button"
              disabled={submitting || body.trim().length === 0}
              onClick={handleSubmit}
              className="rounded-md px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-50"
              style={{ background: "var(--color-brand)" }}
            >
              {submitting ? t("comments.posting") : t("comments.postComment")}
            </button>
          </div>
        </div>
      ) : (
        <p className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-[12px] text-[var(--color-text3)]">
          <a
            href={`/login?next=/markets/${marketSlug}`}
            className="font-bold text-[var(--color-brand)] hover:underline"
          >
            {t("comments.signIn")}
          </a>
          {t("comments.signInSuffix")}
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mb-3 text-[12px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      {/* Thread */}
      {loading && comments.length === 0 && (
        <div
          role="status"
          aria-label={t("comments.loadingAria")}
          className="space-y-3"
        >
          <Skeleton className="h-16 w-full rounded-md" />
          <Skeleton className="h-16 w-full rounded-md" />
          <Skeleton className="h-16 w-full rounded-md" />
        </div>
      )}

      {!loading && comments.length === 0 && !error && (
        <p className="rounded-md border border-dashed border-[var(--color-border)] p-4 text-center text-[12px] text-[var(--color-text3)]">
          {t("comments.empty")}
        </p>
      )}

      <ul className="space-y-3">
        {comments.map((c) => (
          <li key={c.id}>
            <Comment
              row={c}
              authed={authed === true}
              onVote={handleVote}
              onDelete={handleDelete}
              marketId={marketId}
              marketSlug={marketSlug}
              currentUserId={currentUserId}
            />
          </li>
        ))}
      </ul>

      {nextCursor && (
        <button
          type="button"
          disabled={loading}
          onClick={loadMore}
          className="mt-3 w-full rounded-md border border-[var(--color-border)] py-2 text-[12px] font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)]"
        >
          {loading ? t("comments.loading") : t("comments.loadMore")}
        </button>
      )}
    </section>
  );
}

function applyOptimisticVote(c: CommentRow, value: 1 | -1): CommentRow {
  const prev = c.myVote;
  let nextVote: -1 | 0 | 1;
  let scoreDelta: number;
  if (prev === value) {
    nextVote = 0;
    scoreDelta = -value;
  } else if (prev === 0) {
    nextVote = value;
    scoreDelta = value;
  } else {
    nextVote = value;
    scoreDelta = value * 2;
  }
  return { ...c, score: c.score + scoreDelta, myVote: nextVote };
}

function SortPill({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border px-2 py-0.5 font-bold uppercase tracking-[0.4px] transition-colors"
      style={{
        // 2026-05-25 — same forest palette as PostComments SortPill,
        // BetaBanner, and AnnouncementBanner info-severity so all
        // discussion-tier surfaces read as one family.
        borderColor: active ? "#2d3b2e" : "var(--color-border)",
        color: active ? "#2d3b2e" : "var(--color-text3)",
        background: active ? "rgba(45, 59, 46, 0.10)" : "transparent",
      }}
    >
      {label}
    </button>
  );
}

// EPIC-043 — depth cap mirrors the backend `discussion.threading_max_depth`
// default (2). Level 1 = top-level; level 2 = direct reply. A reply on
// a level-2 comment would hit the backend's depth-cap 400, so we hide
// the Reply button at level 2 + render the existing replies flat
// alongside their parent rather than nesting further.
const COMMENT_MAX_DEPTH = 2;

function Comment({
  row,
  authed,
  onVote,
  onDelete,
  marketId,
  marketSlug,
  currentUserId,
  depth = 1,
}: {
  row: CommentRow;
  authed: boolean;
  onVote: (id: string, value: 1 | -1) => void;
  onDelete: (id: string) => void;
  marketId: string;
  marketSlug: string;
  currentUserId: string | null;
  depth?: number;
}) {
  const { t } = useTranslation();
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState<CommentRow[]>([]);
  const [repliesNextCursor, setRepliesNextCursor] = useState<string | null>(null);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const initial = useMemo(
    () => (row.displayName || row.username).charAt(0).toUpperCase(),
    [row.displayName, row.username],
  );

  async function loadReplies() {
    if (repliesLoading) return;
    setRepliesLoading(true);
    try {
      const res = await api.listCommentReplies(row.id);
      setReplies(res.comments);
      setRepliesNextCursor(res.nextCursor);
      setShowReplies(true);
    } catch {
      // Soft-fail: leave the toggle in its previous state.
    } finally {
      setRepliesLoading(false);
    }
  }

  async function postReply() {
    const trimmed = replyBody.trim();
    if (!trimmed || replying) return;
    setReplying(true);
    try {
      const created = await api.createMarketComment(marketId, {
        body: trimmed,
        parentCommentId: row.id,
      });
      setReplies((prev) => [...prev, created]);
      setReplyBody("");
      setShowReplyForm(false);
      setShowReplies(true);
    } catch {
      // Surface up to the parent? The parent error state lives at
      // section level; for replies we keep failures local-silent for
      // closed beta — the user can retry.
    } finally {
      setReplying(false);
    }
  }

  const isOwn = currentUserId !== null && row.userId === currentUserId;

  return (
    <article className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
      <header className="flex items-baseline gap-2">
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
          style={{ background: "linear-gradient(135deg, #8957e5, #ec4899)" }}
          aria-hidden="true"
        >
          {initial}
        </span>
        <span className="text-[12px] font-bold text-[var(--color-text)]">
          {row.displayName || row.username}
        </span>
        {row.displayName && (
          <span className="text-[10px] text-[var(--color-text3)]">
            @{row.username}
          </span>
        )}
        {row.authorPosition && <PositionBadge p={row.authorPosition} />}
        <span className="text-[10px] text-[var(--color-text3)]">
          · {fmtRelative(row.createdAt, t)}
        </span>
      </header>

      <CommentBody row={row} />

      <footer className="mt-2 flex items-center gap-2 text-[11px]">
        <VoteButton
          direction="up"
          active={row.myVote === 1}
          onClick={() => onVote(row.id, 1)}
          disabled={!authed || row.isDeleted || row.isHiddenByAdmin}
        />
        <span
          className="min-w-[20px] text-center font-mono tabular-nums"
          style={{
            color:
              row.score > 0
                ? "var(--color-yes)"
                : row.score < 0
                  ? "var(--color-no)"
                  : "var(--color-text3)",
          }}
        >
          {row.score}
        </span>
        <VoteButton
          direction="down"
          active={row.myVote === -1}
          onClick={() => onVote(row.id, -1)}
          disabled={!authed || row.isDeleted || row.isHiddenByAdmin}
        />
        {authed &&
          !row.isDeleted &&
          !row.isHiddenByAdmin &&
          depth < COMMENT_MAX_DEPTH && (
            <button
              type="button"
              onClick={() => setShowReplyForm((v) => !v)}
              className="ml-2 text-[var(--color-text3)] hover:text-[var(--color-text)]"
            >
              {t("comments.reply")}
            </button>
          )}
        {row.replyCount > 0 && (
          <button
            type="button"
            onClick={() => (showReplies ? setShowReplies(false) : loadReplies())}
            className="text-[var(--color-text3)] hover:text-[var(--color-text)]"
          >
            {showReplies ? t("comments.hide") : t("comments.show")}{" "}
            {row.replyCount}{" "}
            {row.replyCount === 1
              ? t("comments.replyOne")
              : t("comments.replyOther")}
          </button>
        )}
        {isOwn && !row.isDeleted && (
          <button
            type="button"
            onClick={() => onDelete(row.id)}
            className="ml-auto text-[var(--color-text3)] hover:text-[var(--color-no)]"
          >
            {t("comments.delete")}
          </button>
        )}
        {!row.isDeleted && !row.isHiddenByAdmin && (
          <span className="ml-auto">
            <MarketCommentOverflowMenu
              marketSlug={marketSlug}
              commentId={row.id}
              commentExcerpt={row.body ?? null}
              isOwnComment={isOwn}
              authed={authed}
            />
          </span>
        )}
      </footer>

      {showReplyForm && (
        <div className="mt-2 ml-6 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value.slice(0, 5_000))}
            disabled={replying}
            rows={2}
            placeholder={t("comments.replyToTemplate", {
              name: row.displayName || row.username,
            })}
            className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[12px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
          />
          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowReplyForm(false);
                setReplyBody("");
              }}
              disabled={replying}
              className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[10px] font-bold text-[var(--color-text2)]"
            >
              {t("comments.cancel")}
            </button>
            <button
              type="button"
              onClick={postReply}
              disabled={replying || replyBody.trim().length === 0}
              className="rounded-md px-2 py-1 text-[10px] font-bold text-white disabled:opacity-50"
              style={{ background: "var(--color-brand)" }}
            >
              {replying ? t("comments.posting") : t("comments.post")}
            </button>
          </div>
        </div>
      )}

      {showReplies && replies.length > 0 && (
        <ul className="mt-3 ml-6 space-y-2 border-l-2 border-[var(--color-border)] pl-3">
          {replies.map((reply) => (
            <li key={reply.id}>
              <Comment
                row={reply}
                authed={authed}
                onVote={onVote}
                onDelete={onDelete}
                marketId={marketId}
                marketSlug={marketSlug}
                currentUserId={currentUserId}
                depth={depth + 1}
              />
            </li>
          ))}
          {repliesNextCursor && (
            <li>
              <button
                type="button"
                disabled={repliesLoading}
                onClick={async () => {
                  if (!repliesNextCursor) return;
                  setRepliesLoading(true);
                  try {
                    const res = await api.listCommentReplies(row.id, {
                      cursor: repliesNextCursor,
                    });
                    setReplies((prev) => [...prev, ...res.comments]);
                    setRepliesNextCursor(res.nextCursor);
                  } finally {
                    setRepliesLoading(false);
                  }
                }}
                className="text-[11px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
              >
                {repliesLoading
                  ? t("comments.loading")
                  : t("comments.moreReplies")}
              </button>
            </li>
          )}
        </ul>
      )}
    </article>
  );
}

function CommentBody({ row }: { row: CommentRow }) {
  const { t } = useTranslation();
  if (row.isDeleted) {
    return (
      <p className="mt-1 text-[12px] italic text-[var(--color-text3)]">
        {t("comments.removed")}
      </p>
    );
  }
  if (row.isHiddenByAdmin) {
    return (
      <p className="mt-1 text-[12px] italic text-[var(--color-text3)]">
        {t("comments.removedByAdmin")}
      </p>
    );
  }
  const ownOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const previewUrl = extractFirstPlainUrl(
    row.body ?? "",
    ownOrigin ? [ownOrigin] : [],
  );
  return (
    <>
      <MentionParser
        text={row.body ?? ""}
        className="mt-1 block whitespace-pre-wrap text-[13px] text-[var(--color-text2)]"
      />
      {previewUrl && <LinkPreviewCard url={previewUrl} />}
    </>
  );
}

function VoteButton({
  direction,
  active,
  onClick,
  disabled,
}: {
  direction: "up" | "down";
  active: boolean;
  onClick: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const color =
    direction === "up" ? "var(--color-yes)" : "var(--color-no)";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={
        direction === "up" ? t("comments.upvote") : t("comments.downvote")
      }
      className="rounded-md border px-1.5 py-0.5 disabled:opacity-50"
      style={{
        borderColor: active ? color : "var(--color-border)",
        background: active
          ? `color-mix(in srgb, ${color} 14%, transparent)`
          : "transparent",
        color: active ? color : "var(--color-text3)",
      }}
    >
      {direction === "up" ? "▲" : "▼"}
    </button>
  );
}

function fmtRelative(iso: string, t: TFunction): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return t("comments.relativeNow");
  if (min < 60) return t("comments.relativeMinutes", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("comments.relativeHours", { count: hr });
  const d = Math.floor(hr / 24);
  if (d < 7) return t("comments.relativeDays", { count: d });
  return new Date(iso).toLocaleDateString();
}

/**
 * Slice 2A — verifiable position-at-comment-time badge. Shown next to
 * the commenter's name when the snapshot exists. Color matches the
 * outcome (green for YES, red for NO). Tooltip clarifies the snapshot
 * semantic so a viewer doesn't read it as the author's CURRENT
 * position.
 *
 * Why this matters: prediction markets are the only social platform
 * that can verify sentiment with money. A "LONG YES · 50" badge is
 * an on-chain receipt — Stocktwits / Twitter cannot build this.
 */
function PositionBadge({ p }: { p: CommentAuthorPosition }) {
  const { t } = useTranslation();
  const isYes = p.outcome === "yes";
  const color = isYes ? "var(--color-yes)" : "var(--color-no)";
  return (
    <span
      title={t("comments.positionTitle")}
      className="inline-flex items-center gap-1 rounded-[3px] border px-1.5 py-[1px] font-mono text-[9px] font-bold uppercase tracking-[0.4px] tabular-nums"
      style={{
        borderColor: color,
        color,
        background: "color-mix(in srgb, " + color + " 10%, transparent)",
      }}
    >
      {t("comments.positionTemplate", {
        side: isYes ? t("comments.positionYes") : t("comments.positionNo"),
        shares: p.shares,
      })}
    </span>
  );
}
